import { useState } from 'react';
import useConfiguracion from '../../hooks/useConfiguracion';
import { textoPaso } from '../../utils/textosReporte';

const TIPOS_DOCUMENTO = ['DNI', 'CE', 'Pasaporte'];

/**
 * Paso 2/5 del reporte: identidad del denunciante (README diseño, sección
 * 5.3). El "tipo de documento" es una clasificación solo de UI para validar
 * el largo esperado — `Incidencia.inc_chNumDoc` en el backend sigue siendo
 * un texto libre, no se agrega un campo nuevo por esto.
 * Reglas de validación (mismas que ya exigía `AlertForm.jsx`, con nueva
 * presentación): DNI 8 dígitos, teléfono 9 dígitos, correo con formato si
 * se llena, consentimiento obligatorio solo en modo identificado.
 *
 * Elección previa (HU-008, corrección post-cierre): antes "Anónimo"/"Con
 * mis datos" era un selector chico (segmentado) arriba del formulario, y
 * los campos de abajo cambiaban solos al tocarlo — generaba confusión
 * sobre cuál modo estaba activo. Ahora es una pantalla propia con dos
 * botones grandes; recién al elegir uno se entra al formulario
 * correspondiente (`eleccionHecha`).
 */
const PasoDatos = ({ datos, onCambiar, onContinuar, onVolver }) => {
  const [errores, setErrores] = useState({});
  const [eleccionHecha, setEleccionHecha] = useState(false);
  const { configuracion } = useConfiguracion();
  const texto = textoPaso(configuracion, 'datos', {
    titulo: '¿Cómo deseas reportar?',
    subtitulo: 'Elige una opción para continuar.',
  });

  const set = (campo, valor) => onCambiar({ ...datos, [campo]: valor });

  const elegir = (esAnonimo) => {
    onCambiar({ ...datos, esAnonimo });
    setEleccionHecha(true);
  };

  const validar = () => {
    const nuevosErrores = {};

    if (!datos.esAnonimo) {
      const largoEsperado = datos.tipoDocumento === 'DNI' ? 8 : null;
      if (!datos.numeroDocumento) {
        nuevosErrores.numeroDocumento = 'Ingresa tu número de documento';
      } else if (largoEsperado && datos.numeroDocumento.length !== largoEsperado) {
        nuevosErrores.numeroDocumento = `El DNI debe tener ${largoEsperado} dígitos`;
      }
      if (!datos.nombres) nuevosErrores.nombres = 'Ingresa tus nombres';
      if (!datos.apellidos) nuevosErrores.apellidos = 'Ingresa tus apellidos';
      if (!datos.consentimiento) {
        nuevosErrores.consentimiento = 'Debes aceptar para continuar con tus datos';
      }
    }

    if (datos.telefono && datos.telefono.length !== 9) {
      nuevosErrores.telefono = 'El teléfono debe tener 9 dígitos';
    }
    if (datos.correo && !/^\S+@\S+\.\S+$/.test(datos.correo)) {
      nuevosErrores.correo = 'Ingresa un correo válido';
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleContinuar = () => {
    if (validar()) onContinuar();
  };

  if (!eleccionHecha) {
    return (
      <div className="rw-paso">
        <h2 className="rw-titulo" style={texto.estilo}>{texto.titulo}</h2>
        <p className="rw-subtitulo" style={texto.estilo}>{texto.subtitulo}</p>

        <div className="rw-eleccion-datos">
          <button type="button" className="rw-eleccion-tarjeta" onClick={() => elegir(true)}>
            <i className="fas fa-user-secret"></i>
            <strong>Anónimo</strong>
            <span>No pedimos tu identidad. Puedes dejar un alias y un contacto opcional.</span>
          </button>
          <button type="button" className="rw-eleccion-tarjeta" onClick={() => elegir(false)}>
            <i className="fas fa-id-card"></i>
            <strong>Con mis datos</strong>
            <span>Dejas tu nombre y documento — útil si quieres que te contacten por este caso.</span>
          </button>
        </div>

        <div className="rw-pie-fijo rw-pie-con-volver">
          <button type="button" className="rw-boton-secundario" onClick={onVolver}>Atrás</button>
        </div>
      </div>
    );
  }

  return (
    <div className="rw-paso">
      <h2 className="rw-titulo">{datos.esAnonimo ? 'Tus datos (anónimo)' : 'Tus datos'}</h2>

      {!datos.esAnonimo ? (
        <>
          <div className="rw-fila">
            <select
              className="rw-select-tipo-doc"
              value={datos.tipoDocumento}
              onChange={(e) => set('tipoDocumento', e.target.value)}
            >
              {TIPOS_DOCUMENTO.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input
              type="text"
              inputMode="numeric"
              placeholder="Número de documento"
              value={datos.numeroDocumento}
              onChange={(e) => set('numeroDocumento', e.target.value.replace(/\D/g, ''))}
            />
          </div>
          {errores.numeroDocumento && <p className="rw-error">{errores.numeroDocumento}</p>}

          <div className="rw-fila">
            <div>
              <label className="rw-label">Nombres *</label>
              <input type="text" value={datos.nombres} onChange={(e) => set('nombres', e.target.value)} />
              {errores.nombres && <p className="rw-error">{errores.nombres}</p>}
            </div>
            <div>
              <label className="rw-label">Apellidos *</label>
              <input type="text" value={datos.apellidos} onChange={(e) => set('apellidos', e.target.value)} />
              {errores.apellidos && <p className="rw-error">{errores.apellidos}</p>}
            </div>
          </div>

          <label className="rw-label">Teléfono</label>
          <div className="rw-input-prefijo">
            <span>+51</span>
            <input
              type="tel"
              inputMode="numeric"
              value={datos.telefono}
              onChange={(e) => set('telefono', e.target.value.replace(/\D/g, ''))}
            />
          </div>
          {errores.telefono && <p className="rw-error">{errores.telefono}</p>}

          <label className="rw-label">Correo (opcional)</label>
          <input type="email" value={datos.correo} onChange={(e) => set('correo', e.target.value)} />
          {errores.correo && <p className="rw-error">{errores.correo}</p>}

          <label className="rw-checkbox-consentimiento">
            <input
              type="checkbox"
              checked={datos.consentimiento}
              onChange={(e) => set('consentimiento', e.target.checked)}
            />
            <span>Autorizo que mis datos se usen para dar seguimiento a este reporte
              (visible solo para personal con el permiso <code>usuarios.ver_datos</code>).</span>
          </label>
          {errores.consentimiento && <p className="rw-error">{errores.consentimiento}</p>}
        </>
      ) : (
        <>
          <label className="rw-label">Alias (opcional)</label>
          <input type="text" value={datos.alias} onChange={(e) => set('alias', e.target.value)} />

          <div className="rw-tarjeta-opcional">
            <span className="rw-tarjeta-opcional-titulo">
              <i className="fas fa-shield-halved"></i> Datos de contacto (opcional)
            </span>
            <p className="rw-texto-ayuda">
              Sin datos de identidad — solo para poder avisarte si necesitamos más detalles.
            </p>

            <label className="rw-label">Teléfono</label>
            <div className="rw-input-prefijo">
              <span>+51</span>
              <input
                type="tel"
                inputMode="numeric"
                value={datos.telefono}
                onChange={(e) => set('telefono', e.target.value.replace(/\D/g, ''))}
              />
            </div>
            {errores.telefono && <p className="rw-error">{errores.telefono}</p>}

            <label className="rw-label">Correo</label>
            <input type="email" value={datos.correo} onChange={(e) => set('correo', e.target.value)} />
            {errores.correo && <p className="rw-error">{errores.correo}</p>}
          </div>

          <div className="rw-aviso-ambar">
            Guarda bien tu código de seguimiento: sin datos de contacto, es la única forma
            de recuperar tu ticket.
          </div>
        </>
      )}

      <div className="rw-pie-fijo rw-pie-con-volver">
        <button type="button" className="rw-boton-secundario" onClick={() => setEleccionHecha(false)}>Atrás</button>
        <button type="button" className="rw-boton-primario" onClick={handleContinuar}>Continuar</button>
      </div>
    </div>
  );
};

export default PasoDatos;
